import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Tooltip,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Add from "@mui/icons-material/Add";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Close from "@mui/icons-material/Close";
import Code from "@mui/icons-material/Code";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { VARIAVEIS_CONTEXTO, getVariablesByGroup } from "./chatbotContextVariables";
import ContextVariableAutocomplete from "./ContextVariableAutocomplete";
import {
  WHATSAPP_REPLY_BUTTON_TITLE_MAX,
  WHATSAPP_MAX_REPLY_BUTTONS,
  WHATSAPP_MAX_LIST_ROWS,
  WHATSAPP_MAX_LINK_BUTTONS,
  validateChatbotButtonLabels,
  normalizeChatbotButtonsForSave,
  getChatbotButtonWhatsAppDeliveryMode,
} from "./chatbotWhatsAppLimits";
import { partitionChatbotButtonEntries, parseChatbotButtonEntries, splitStoredButton } from "../../utils/chatbotButtonLinks";
import ChatbotButtonLinkHelpDialog from "./ChatbotButtonLinkHelpDialog";
import ChatbotStepActionEditor from "./ChatbotStepActionEditor";
import ChatbotParentSlotPicker from "./ChatbotParentSlotPicker";
import { useChatbotActions, useChatbotSlots } from "../../hooks/useChatbot";
import { useAutomationCatalog } from "../../hooks/useAutomationRules";
import { enrichChatbotNavigationActions, findDuplicateNomeSlot } from "../../utils/chatbotNavigationActions";
import {
  EVENTO_IDENTIFICADOR,
  EVENTO_INATIVIDADE,
  defaultSlotEvento,
  eventoFromSlot,
  eventoToPayload,
} from "../../utils/chatbotSlotEvent";
import { ensureAlterarSolicitacaoAction } from "../../utils/chatbotAlterarSolicitacaoAction";
import { acoesFromSlot, acoesToPayload, legacyFieldsFromAcoes } from "../../utils/chatbotSlotActions";
import { useS3Upload } from "../../hooks/useS3Upload";
import ChatbotSlotFaqSection from "./ChatbotSlotFaqSection";
import {
  defaultFaqIaConfig,
  faqConfigFromSlot,
  faqConfigToPayload,
  defaultAgenteIaConfig,
  RESPOSTA_MODO_FAQ_IA,
  RESPOSTA_MODO_TEXTO,
  RESPOSTA_MODO_AGENTE_IA,
  VALIDACAO_RESPOSTA_NONE,
  VALIDACAO_RESPOSTA_NUMERO_RANGE,
} from "../../utils/chatbotSlotFaq";
import {
  VALIDACAO_RESPOSTA_TYPES,
  VALIDACAO_RESPOSTA_LABEL_KEYS,
  slotExpectsFreeTextInput,
  validationSupportsCadastroLookup,
} from "../../utils/chatbotSlotInputValidation";
import { useTranslation } from "react-i18next";
import { isGlobalExceptionSlot, hasConfiguredGlobalException, isLocalExceptionSlot, isFlowStartSlot } from "./chatbotSlotParentUtils";
import ChatbotIdentificadoresInput from "./ChatbotIdentificadoresInput";
import { buildCondicoesString, parseCondicoesString } from "../../utils/chatbotCondicoes";
import {
  CHATBOT_HORARIO_DAYS,
  clearAllHorarioFields,
  dayHorarioActive,
  formHasHorarioRestriction,
  horarioFieldsFromSlot,
} from "../../utils/chatbotHorarios";

function botoesFromSlot(rawBotoes) {
  const items = Array.isArray(rawBotoes)
    ? rawBotoes
    : rawBotoes
      ? String(rawBotoes).split(",").map((b) => b.trim()).filter(Boolean)
      : [];
  return items.map((item) => {
    const split = splitStoredButton(item);
    return {
      label: split.label,
      url: split.url,
      isLink: split.isLink,
    };
  });
}

function emptyBotao() {
  return { label: "", url: "", isLink: false };
}

function ConditionsEditor({ form, setForm, isFlowStart = false }) {
  const [rows, setRows] = useState(() => parseCondicoesString(form.condicoes));

  // Só sincroniza do form → rows (ex.: troca de slot). Não reescreve o form no mount —
  // o antigo efeito rows→form apagava condicoes salvas quando rows ainda estavam vazias.
  // Se o build das rows atuais já bate com form.condicoes, mantém rascunhos incompletos.
  useEffect(() => {
    const incoming = String(form.condicoes || "");
    setRows((prev) => {
      if (buildCondicoesString(prev) === incoming) return prev;
      return parseCondicoesString(incoming);
    });
  }, [form.condicoes]);

  const commitRows = (nextRows) => {
    setRows(nextRows);
    const str = buildCondicoesString(nextRows);
    setForm((f) => (f.condicoes === str ? f : { ...f, condicoes: str }));
  };

  const updateRow = (idx, patch) => {
    commitRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    commitRows([
      ...rows,
      {
        variavel: "",
        operador: "igual",
        valor: "",
        andOr: rows.length ? "and" : "",
      },
    ]);
  };

  const removeRow = (idx) => {
    commitRows(rows.filter((_, i) => i !== idx));
  };

  const operadores = [
    { value: "igual", label: "Igual" },
    { value: "menor", label: "Menor" },
    { value: "maior", label: "Maior" },
    { value: "menorigual", label: "Menor ou igual" },
    { value: "maiorigual", label: "Maior ou igual" },
    { value: "comecacom", label: "Começa com" },
    { value: "terminacom", label: "Termina com" },
    { value: "contem", label: "Contém" },
  ];

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        Monte regras sobre o contexto do fluxo. Busque a variável pelo nome ou descrição (ex.:{" "}
        <code>cadastroencontrado</code>, <code>lgpd</code>, <code>cpf</code>).
        {isFlowStart ? (
          <>
            {" "}
            No passo inicial isso define se ele aparece na primeira mensagem (ex.: <code>lgpd</code> igual a{" "}
            <code>0</code> para pedir o aceite só quando ainda não houver).
          </>
        ) : null}
      </Typography>
      <Stack spacing={1.5}>
        {rows.map((row, idx) => (
          <Box key={idx} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {idx > 0 && (
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel>Lógico</InputLabel>
                <Select
                  label="Lógico"
                  value={row.andOr || "and"}
                  onChange={(e) => updateRow(idx, { andOr: e.target.value })}
                >
                  <MenuItem value="and">E (AND)</MenuItem>
                  <MenuItem value="or">Ou (OR)</MenuItem>
                </Select>
              </FormControl>
            )}
            <ContextVariableAutocomplete
              value={row.variavel}
              onChange={(name) => updateRow(idx, { variavel: name })}
              label="Variável"
              placeholder="Ex.: cadastro, cpf, data..."
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={row.operador}
                onChange={(e) => updateRow(idx, { operador: e.target.value })}
              >
                {operadores.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Valor"
              value={row.valor}
              onChange={(e) => updateRow(idx, { valor: e.target.value })}
              sx={{ minWidth: 120, flex: 1 }}
            />
            <IconButton size="small" color="error" onClick={() => removeRow(idx)}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={addRow}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          Adicionar condição
        </Button>
      </Stack>
    </Box>
  );
}

export default function ChatbotNodePanel({
  slot,
  onClose,
  onSave,
  onDelete,
  onAddSubpasso,
  onAddException,
  isSaving,
  isDeleting,
  isAddingSubpasso = false,
  saveError,
  canDelete = true,
  presentation = "drawer",
  onNavigateToSlot,
}) {
  const { t } = useTranslation("settings");
  const faqSectionRef = useRef(null);
  const [form, setForm] = useState({
    identificador: "",
    nome: "",
    resposta: "",
    botoes: [],
    condicoes: "",
    acoes: [],
    id_pai: 0,
    global: false,
    ignorarglobal: false,
    horario_seg_ini: "",
    horario_seg_fim: "",
    horario_ter_ini: "",
    horario_ter_fim: "",
    horario_qua_ini: "",
    horario_qua_fim: "",
    horario_qui_ini: "",
    horario_qui_fim: "",
    horario_sex_ini: "",
    horario_sex_fim: "",
    horario_sab_ini: "",
    horario_sab_fim: "",
    horario_dom_ini: "",
    horario_dom_fim: "",
    evento: defaultSlotEvento(),
    resposta_modo: RESPOSTA_MODO_TEXTO,
    faq_ia: defaultFaqIaConfig(),
    agente_ia: defaultAgenteIaConfig(),
    validacao_resposta: VALIDACAO_RESPOSTA_NONE,
    validacao_numero_min: "",
    validacao_numero_max: "",
    verificar_cadastro: false,
    obrigatorio: false,
  });
  const [touched, setTouched] = useState({ global: false, ignorarglobal: false });
  const [localSaveError, setLocalSaveError] = useState(null);
  const [linkHelpOpen, setLinkHelpOpen] = useState(false);

  const whatsappDeliveryMode = useMemo(
    () => getChatbotButtonWhatsAppDeliveryMode(form.botoes),
    [form.botoes]
  );
  const replyButtonCount = useMemo(() => {
    const entries = (form.botoes || []).map((item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        if (item.isLink || String(item.url || "").trim()) return item;
        return String(item.label || "").trim();
      }
      return item;
    });
    return partitionChatbotButtonEntries(parseChatbotButtonEntries(entries)).replies.length;
  }, [form.botoes]);
  const linkButtonCount = useMemo(
    () => (form.botoes || []).filter((b) => b && (b.isLink || String(b.url || "").trim())).length,
    [form.botoes]
  );
  const hasLinkButton = linkButtonCount >= WHATSAPP_MAX_LINK_BUTTONS;

  useEffect(() => {
    if (!slot) return;
    const hasGlobal = slot.global !== null && slot.global !== undefined;
    const hasIgnorarGlobal = slot.ignorarglobal !== null && slot.ignorarglobal !== undefined;
    const faqCfg = faqConfigFromSlot(slot);
    setForm({
      identificador: slot.identificador ?? "",
      nome: slot.nome ?? "",
      resposta: slot.resposta ?? "",
      botoes: botoesFromSlot(slot.botoes),
      condicoes: slot.condicoes ?? "",
      acoes: acoesFromSlot(slot),
      id_pai: slot.id_pai != null ? Number(slot.id_pai) : 0,
      global: hasGlobal ? !!slot.global : false,
      ignorarglobal: hasIgnorarGlobal ? !!slot.ignorarglobal : false,
      ...horarioFieldsFromSlot(slot),
      evento: eventoFromSlot(slot),
      resposta_modo: faqCfg.resposta_modo,
      faq_ia: faqCfg.faq_ia,
      agente_ia: faqCfg.agente_ia,
      validacao_resposta: faqCfg.validacao_resposta || VALIDACAO_RESPOSTA_NONE,
      validacao_numero_min:
        faqCfg.validacao_numero_min != null ? String(faqCfg.validacao_numero_min) : "",
      validacao_numero_max:
        faqCfg.validacao_numero_max != null ? String(faqCfg.validacao_numero_max) : "",
      verificar_cadastro: !!faqCfg.verificar_cadastro,
      obrigatorio: !!faqCfg.obrigatorio,
    });
    setTouched({ global: hasGlobal, ignorarglobal: hasIgnorarGlobal });
    setLocalSaveError(null);
  }, [slot]);

  const addBotao = () => {
    setLocalSaveError(null);
    setForm((f) => ({ ...f, botoes: [...(f.botoes || []), emptyBotao()] }));
  };
  const removeBotao = (i) => {
    setLocalSaveError(null);
    setForm((f) => ({ ...f, botoes: f.botoes.filter((_, j) => j !== i) }));
  };
  const changeBotaoLabel = (i, value) => {
    setLocalSaveError(null);
    const clipped = String(value).slice(0, WHATSAPP_REPLY_BUTTON_TITLE_MAX);
    setForm((f) => ({
      ...f,
      botoes: f.botoes.map((b, j) => (j === i ? { ...b, label: clipped } : b)),
    }));
  };
  const changeBotaoUrl = (i, value) => {
    setLocalSaveError(null);
    const url = String(value).trim();
    const enablingLink = Boolean(url);
    if (enablingLink) {
      const otherHasLink = (form.botoes || []).some(
        (b, j) => j !== i && b && (b.isLink || String(b.url || "").trim())
      );
      if (otherHasLink) {
        setLocalSaveError(
          `Máximo de ${WHATSAPP_MAX_LINK_BUTTONS} botão com link por passo (limite do WhatsApp).`
        );
        return;
      }
    }
    setForm((f) => ({
      ...f,
      botoes: f.botoes.map((b, j) =>
        j === i ? { ...b, url, isLink: enablingLink } : b
      ),
    }));
  };
  const toggleBotaoLink = (i, checked) => {
    setLocalSaveError(null);
    if (checked) {
      const otherHasLink = (form.botoes || []).some(
        (b, j) => j !== i && b && (b.isLink || String(b.url || "").trim())
      );
      if (otherHasLink) {
        setLocalSaveError(
          `Máximo de ${WHATSAPP_MAX_LINK_BUTTONS} botão com link por passo (limite do WhatsApp).`
        );
        return;
      }
    }
    setForm((f) => ({
      ...f,
      botoes: f.botoes.map((b, j) =>
        j === i ? { ...b, isLink: checked, url: checked ? b.url : "" } : b
      ),
    }));
  };

  const respostaInputRef = useRef(null);
  const lastRespostaSelectionRef = useRef({ start: 0, end: 0 });

  const handleInsertVariable = useCallback(
    (varName) => {
      const input = respostaInputRef.current;
      let start = lastRespostaSelectionRef.current.start;
      let end = lastRespostaSelectionRef.current.end;
      if (input && document.activeElement === input) {
        start = input.selectionStart ?? start;
        end = input.selectionEnd ?? end;
      }
      const current = form.resposta || "";
      const newValue = current.slice(0, start) + varName + current.slice(end);
      setForm((f) => ({ ...f, resposta: newValue }));
      lastRespostaSelectionRef.current = { start: start + varName.length, end: start + varName.length };
      setTimeout(() => {
        if (respostaInputRef.current) {
          respostaInputRef.current.focus();
          respostaInputRef.current.setSelectionRange(
            lastRespostaSelectionRef.current.start,
            lastRespostaSelectionRef.current.end
          );
        }
      }, 0);
    },
    [form.resposta]
  );

  const [showVars, setShowVars] = useState(false);
  const { byGroup, groupLabels } = getVariablesByGroup();
  const chatbotUserId = slot?.usuario || slot?.user_id || null;
  const { data: acoesUra = [], isLoading: isLoadingAcoes } = useChatbotActions(chatbotUserId);
  const { data: allSlots = [] } = useChatbotSlots(chatbotUserId);
  const isFlowStart = useMemo(() => isFlowStartSlot(slot, allSlots), [slot, allSlots]);
  const { data: automationMeta } = useAutomationCatalog(1);
  const alterarSolicitacaoForm = useMemo(() => {
    const acoes = automationMeta?.data?.acoes ?? automationMeta?.acoes ?? [];
    const def = acoes.find((a) => Number(a.id) === 4);
    return def?.form || null;
  }, [automationMeta]);
  const acoesEnriched = useMemo(() => {
    const withNav = enrichChatbotNavigationActions(acoesUra, allSlots, slot?.id ?? null);
    return ensureAlterarSolicitacaoAction(withNav, alterarSolicitacaoForm);
  }, [acoesUra, allSlots, alterarSolicitacaoForm, slot?.id]);
  const { uploadOne } = useS3Upload();
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleSave = async () => {
    if (!slot) return;
    const botoesErr = validateChatbotButtonLabels(form.botoes);
    if (botoesErr) {
      setLocalSaveError(botoesErr);
      return;
    }
    const duplicateNome = findDuplicateNomeSlot(form.nome, slot.id, allSlots);
    if (duplicateNome) {
      setLocalSaveError(
        t("chatbotConfigPage.duplicateNomeError", {
          nome: String(form.nome || "").trim(),
          slotId: duplicateNome.id,
        })
      );
      return;
    }
    setLocalSaveError(null);

    let faqIaPrepared = form.faq_ia;
    try {
      if (faqSectionRef.current?.prepareForSave) {
        faqIaPrepared = await faqSectionRef.current.prepareForSave();
      }
    } catch (e) {
      setLocalSaveError(e?.message || "Falha ao enviar documentos da base de conhecimento.");
      return;
    }

    if (form.validacao_resposta === VALIDACAO_RESPOSTA_NUMERO_RANGE) {
      const min = String(form.validacao_numero_min ?? "").trim();
      const max = String(form.validacao_numero_max ?? "").trim();
      if (min === "" || max === "" || !Number.isFinite(Number(min.replace(",", "."))) || !Number.isFinite(Number(max.replace(",", ".")))) {
        setLocalSaveError(
          t("chatbotConfigPage.chatbotInputValidation.rangeRequired", {
            defaultValue: "Informe o valor mínimo e máximo para validação numérica em intervalo.",
          })
        );
        return;
      }
      if (Number(min.replace(",", ".")) > Number(max.replace(",", "."))) {
        setLocalSaveError(
          t("chatbotConfigPage.chatbotInputValidation.rangeOrder", {
            defaultValue: "O valor mínimo deve ser menor ou igual ao máximo.",
          })
        );
        return;
      }
    }

    const legacy = legacyFieldsFromAcoes(form.acoes);
    const faqPayload = faqConfigToPayload(
      form.resposta_modo,
      faqIaPrepared,
      form.agente_ia,
      form.validacao_resposta,
      form.validacao_numero_min,
      form.validacao_numero_max,
      form.verificar_cadastro,
      form.obrigatorio
    );
    const payload = {
      id: slot.id,
      ...form,
      ...faqPayload,
      acoes: acoesToPayload(form.acoes),
      tipo_acao: legacy.tipo_acao,
      id_acao: legacy.id_acao,
      botoes: normalizeChatbotButtonsForSave(form.botoes),
    };

    delete payload.value;

    // Não define Global/Ignorar Global automaticamente em subpassos novos:
    // só envia esses campos se o usuário mexeu neles, ou se já vinham definidos no slot.
    if (!touched.global) delete payload.global;
    if (!touched.ignorarglobal) delete payload.ignorarglobal;

    if (form.global) {
      const eventoPayload = eventoToPayload(form.evento);
      if (eventoPayload) payload.evento = eventoPayload;
      else payload.evento = { tipo: EVENTO_IDENTIFICADOR };
    }

    const originalParent = slot.id_pai != null ? Number(slot.id_pai) : 0;
    const nextParent = Number(form.id_pai) || 0;
    if (nextParent !== originalParent) {
      payload.id_pai = nextParent;
    } else {
      delete payload.id_pai;
    }

    if (isFlowStartSlot(slot, allSlots)) {
      payload.global = false;
      payload.evento = { tipo: EVENTO_IDENTIFICADOR };
      delete payload.id_pai;
    }

    onSave(payload);
  };

  const handleSelectFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    try {
      setIsUploadingFile(true);
      const prefix = `workspace-chatbot/ura/${slot?.usuario || "default"}`;
      const { url } = await uploadOne(file, { prefix });
      setForm((f) => ({ ...f, arquivo: url }));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.message || "Falha ao enviar arquivo.");
    } finally {
      setIsUploadingFile(false);
      event.target.value = "";
    }
  };

  const handleDelete = () => {
    if (!slot || !canDelete) return;
    setConfirmDeleteOpen(true);
  };

  if (!slot) return null;

  const isExcecao = slot.identificador === "Exceção";
  const hasWebchatInviteAction = (form.acoes || []).some(
    (acao) => Number(acao?.tipo_acao) === 1 && Number(acao?.id_acao) === 91
  );
  const isGlobalExcecao = isGlobalExceptionSlot(slot, allSlots);
  const showInputValidation =
    !isFlowStart &&
    slotExpectsFreeTextInput({
      identificador: form.identificador,
      replyButtonCount,
    });
  const isLocalExcecao = isLocalExceptionSlot(slot, allSlots);
  const globalExceptionReady = hasConfiguredGlobalException(allSlots);

  const panelBody = (
    <>
      {/* Cabeçalho fixo: título, adicionar subpasso (sem fechar o painel) e fechar */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          pt: 2,
          pb: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: isExcecao ? "warning.light" : "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {isExcecao ? "Slot de Exceção" : isFlowStart ? "Editar passo inicial" : "Editar passo"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {onAddSubpasso && (
            <Button
              variant="contained"
              size="small"
              startIcon={isAddingSubpasso ? <CircularProgress size={16} color="inherit" /> : <Add />}
              onClick={onAddSubpasso}
              disabled={isAddingSubpasso}
              sx={{ textTransform: "none" }}
            >
              Adicionar subpasso
            </Button>
          )}
          {onAddException && (
            <Button
              variant="outlined"
              size="small"
              onClick={onAddException}
              disabled={isAddingSubpasso}
              sx={{ textTransform: "none" }}
            >
              Exceção neste nível
            </Button>
          )}
          <IconButton onClick={onClose} size="small" aria-label="Fechar" sx={{ ml: 0.5 }}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* Conteúdo rolável com padding generoso */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 3,
          py: 2.5,
          pb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        {(saveError || localSaveError) && (
          <Alert severity="error" onClose={() => {}} sx={{ mb: 2 }}>
            {saveError || localSaveError}
          </Alert>
        )}

        {isExcecao && (
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            <Typography variant="body2" component="span">
              {isGlobalExcecao ? (
                <>
                  Esta é a <strong>exceção global</strong> do fluxo — usada em todos os níveis quando nenhuma opção
                  válida for encontrada e não houver exceção específica naquele nível.
                </>
              ) : (
                <>
                  Exceção <strong>específica deste nível</strong> — substitui a exceção global aqui quando nenhuma
                  outra opção for válida.{" "}
                  {canDelete &&
                    t("chatbotConfigPage.localExceptionDeleteHint", {
                      defaultValue:
                        "Você pode excluir esta exceção para que o nível volte a usar a exceção global.",
                    })}
                </>
              )}{" "}
              Configure a resposta e os botões que o usuário verá nesse caso.
            </Typography>
          </Alert>
        )}

        {isFlowStart && !isExcecao && (
          <Alert severity="info" sx={{ mb: 2 }} icon={false}>
            <Typography variant="body2">
              A conversa <strong>começa neste passo</strong> na primeira mensagem, sem usar identificador.
              Para não repetir o pedido (ex.: LGPD já aceita no cadastro), use <strong>Condições</strong> nas
              opções avançadas e um irmão na raiz com identificador <code>$lgpd</code>.
            </Typography>
          </Alert>
        )}

        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
          {isExcecao ? "Identificador (fixo)" : "Identificação"}
        </Typography>
        <Stack spacing={2}>
          {isExcecao ? (
            <TextField
              label="Identificador"
              size="small"
              fullWidth
              value={form.identificador}
              helperText='Sempre "Exceção". Não é acionado por texto; apenas quando nenhuma outra opção for válida.'
              disabled
              variant="outlined"
            />
          ) : isFlowStart ? null : (
            <ChatbotIdentificadoresInput
              value={form.identificador}
              onChange={(newValue) => setForm((f) => ({ ...f, identificador: newValue }))}
              disabled={false}
              slot={slot}
              allSlots={allSlots}
              slotNome={form.nome}
            />
          )}
          {!isExcecao && (
            <TextField
              label="Nome (relatórios)"
              size="small"
              fullWidth
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              variant="outlined"
            />
          )}
          <ChatbotParentSlotPicker
            slot={slot}
            allSlots={allSlots}
            value={form.id_pai}
            onChange={(idPai) => setForm((f) => ({ ...f, id_pai: idPai }))}
            disabled={isSaving}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
          {isExcecao ? "Mensagem quando nenhuma opção for válida" : "Mensagem"}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Use variáveis com $nome (ex.: $nomecliente); são substituídas pelo valor no envio. *texto* = negrito.
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            startIcon={<Code />}
            endIcon={showVars ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setShowVars((v) => !v)}
            sx={{ textTransform: "none" }}
          >
            Variáveis de contexto
          </Button>
          <Collapse in={showVars}>
            <Box sx={{ mt: 1, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
              {Object.entries(byGroup).map(([groupKey, vars]) => (
                <Box key={groupKey} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    {groupLabels[groupKey] || groupKey}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {vars.map((v) => (
                      <Tooltip key={v.name} title={`${v.label} — ${v.name}`}>
                        <Chip
                          size="small"
                          label={v.label}
                          onClick={() => handleInsertVariable(v.name)}
                          sx={{ fontSize: "0.75rem" }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Dica: ao passar o mouse, você vê a chave (ex.: <code>$nomecliente</code>) que será inserida na mensagem.
              </Typography>
            </Box>
          </Collapse>
        </Box>
        <TextField
          label={
            form.resposta_modo === RESPOSTA_MODO_FAQ_IA
              ? isExcecao
                ? t("chatbotConfigPage.chatbotSlotFaq.responseFallbackException", { defaultValue: "Resposta fallback (se a IA não responder)" })
                : t("chatbotConfigPage.chatbotSlotFaq.responseFallback", { defaultValue: "Resposta fallback (opcional)" })
              : form.resposta_modo === RESPOSTA_MODO_AGENTE_IA
                ? t("chatbotConfigPage.chatbotSlotFaq.agentResponseFallback", { defaultValue: "Resposta fallback (se o agente não responder)" })
              : isExcecao
                ? "Resposta (ex.: Por favor, escolha uma opção válida)"
                : "Resposta"
          }
          size="small"
          fullWidth
          multiline
          rows={4}
          value={form.resposta}
          onChange={(e) => {
            const el = e.target;
            lastRespostaSelectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
            setForm((f) => ({ ...f, resposta: e.target.value }));
          }}
          onBlur={(e) => {
            lastRespostaSelectionRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
          }}
          inputRef={respostaInputRef}
          variant="outlined"
          sx={{ mb: 1 }}
          placeholder={
            hasWebchatInviteAction
              ? "Ex.: Para continuar, toque no botão e abra o chat em uma nova aba."
              : isExcecao
                ? "Ex.: Por favor, escolha uma opção válida do menu acima."
                : "Ex.: Olá *$nomecliente*! Sua data: $datadigitada"
          }
          helperText={
            hasWebchatInviteAction
              ? "Com a ação Enviar conversa para Web Chat: este texto vai junto com o botão. Se ficar vazio, enviamos a mensagem padrão."
              : undefined
          }
          InputProps={{ sx: { lineHeight: 1.5 } }}
        />

        {showInputValidation && (
          <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
            <InputLabel>
              {t("chatbotConfigPage.chatbotInputValidation.label", { defaultValue: "Validação da resposta" })}
            </InputLabel>
            <Select
              label={t("chatbotConfigPage.chatbotInputValidation.label", { defaultValue: "Validação da resposta" })}
              value={form.validacao_resposta || VALIDACAO_RESPOSTA_NONE}
              onChange={(e) => {
                const next = e.target.value || VALIDACAO_RESPOSTA_NONE;
                setForm((f) => ({
                  ...f,
                  validacao_resposta: next,
                  validacao_numero_min:
                    next === VALIDACAO_RESPOSTA_NUMERO_RANGE ? f.validacao_numero_min : "",
                  validacao_numero_max:
                    next === VALIDACAO_RESPOSTA_NUMERO_RANGE ? f.validacao_numero_max : "",
                }));
              }}
            >
              {VALIDACAO_RESPOSTA_TYPES.map((type) => (
                <MenuItem key={type || "none"} value={type}>
                  {t(
                    `chatbotConfigPage.chatbotInputValidation.types.${VALIDACAO_RESPOSTA_LABEL_KEYS[type] || "none"}`,
                    {
                      defaultValue:
                        type === ""
                          ? "Nenhuma (aceita qualquer texto)"
                          : type.charAt(0).toUpperCase() + type.slice(1).replace("_", " "),
                    }
                  )}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              {t("chatbotConfigPage.chatbotInputValidation.helper", {
                defaultValue:
                  "Valida a mensagem digitada pelo cliente neste passo. Opções de menu clicadas ou digitadas com o identificador exato não passam por esta validação.",
              })}
            </Typography>
          </FormControl>
        )}

        {showInputValidation && validationSupportsCadastroLookup(form.validacao_resposta) && (
          <FormControlLabel
            sx={{ mb: 1.5, alignItems: "flex-start" }}
            control={
              <Checkbox
                checked={!!form.verificar_cadastro}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    verificar_cadastro: e.target.checked,
                  }))
                }
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  {t("chatbotConfigPage.chatbotInputValidation.verifyRegistrationLabel", {
                    defaultValue: "Verificar cadastro na plataforma",
                  })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("chatbotConfigPage.chatbotInputValidation.verifyRegistrationHelper", {
                    defaultValue:
                      "Após validar a resposta, busca o cliente no projeto e preenche $cadastroencontrado (1 = encontrado, 0 = não encontrado) e os dados em $nomecliente, $cpfcliente, etc. Use condições nos subpassos para ramificar o fluxo.",
                  })}
                </Typography>
              </Box>
            }
          />
        )}

        {showInputValidation && form.validacao_resposta === VALIDACAO_RESPOSTA_NUMERO_RANGE && (
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <TextField
              label={t("chatbotConfigPage.chatbotInputValidation.rangeMin", { defaultValue: "Mínimo" })}
              size="small"
              fullWidth
              value={form.validacao_numero_min}
              onChange={(e) => setForm((f) => ({ ...f, validacao_numero_min: e.target.value }))}
              placeholder="1"
            />
            <TextField
              label={t("chatbotConfigPage.chatbotInputValidation.rangeMax", { defaultValue: "Máximo" })}
              size="small"
              fullWidth
              value={form.validacao_numero_max}
              onChange={(e) => setForm((f) => ({ ...f, validacao_numero_max: e.target.value }))}
              placeholder="10"
            />
          </Stack>
        )}

        <ChatbotSlotFaqSection
          ref={faqSectionRef}
          slotKey={slot?.id ?? null}
          respostaModo={form.resposta_modo}
          faqIa={form.faq_ia}
          agenteIa={form.agente_ia}
          onRespostaModoChange={(mode) => setForm((f) => ({ ...f, resposta_modo: mode }))}
          onFaqIaChange={(faqIa) => setForm((f) => ({ ...f, faq_ia: { ...f.faq_ia, ...faqIa } }))}
          onAgenteIaChange={(agenteIa) => setForm((f) => ({ ...f, agente_ia: { ...f.agente_ia, ...agenteIa } }))}
          chatbotUserId={chatbotUserId}
          isExcecao={isExcecao}
          t={t}
        />

        <Typography variant="overline" color="text.secondary" sx={{ mt: 2.5, mb: 0.5, letterSpacing: 0.8 }}>
          Botões
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Resposta rápida (navega no fluxo) ou link clicável (abre URL no WhatsApp).
          Rótulos com no máximo {WHATSAPP_REPLY_BUTTON_TITLE_MAX} caracteres.
          O WhatsApp permite no máximo {WHATSAPP_MAX_LINK_BUTTONS} botão com link por passo.
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          WhatsApp: até {WHATSAPP_MAX_REPLY_BUTTONS} botões nativos; com mais opções de resposta, enviamos
          automaticamente como lista interativa (até {WHATSAPP_MAX_LIST_ROWS}).
        </Typography>
        {whatsappDeliveryMode === "list" && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Este passo tem {replyButtonCount} opções de resposta. No WhatsApp, elas serão enviadas como{" "}
            <strong>lista interativa</strong> (botão &quot;Ver opções&quot;), respeitando o limite da plataforma.
          </Alert>
        )}
        {whatsappDeliveryMode === "text" && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Há mais de {WHATSAPP_MAX_LIST_ROWS} opções de resposta. No WhatsApp, as opções serão incluídas no
            texto da mensagem (sem botões/lista interativa).
          </Alert>
        )}
        {linkButtonCount > WHATSAPP_MAX_LINK_BUTTONS && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            Há {linkButtonCount} botões com link. O WhatsApp permite apenas {WHATSAPP_MAX_LINK_BUTTONS} por
            passo — desative o modo Link nos botões extras antes de salvar.
          </Alert>
        )}
        <Stack spacing={1.5}>
          {(form.botoes || []).map((b, i) => {
            const isLink = Boolean(b.isLink);
            const linkSwitchDisabled = hasLinkButton && !isLink;
            return (
            <Box key={i} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
              <Box display="flex" gap={1} alignItems="center" sx={{ mb: isLink ? 1 : 0 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={b.label || ""}
                  onChange={(e) => changeBotaoLabel(i, e.target.value)}
                  placeholder={isLink ? "Rótulo do link" : "Texto do botão"}
                  variant="outlined"
                  inputProps={{ maxLength: WHATSAPP_REPLY_BUTTON_TITLE_MAX }}
                  helperText={`${String(b.label || "").length}/${WHATSAPP_REPLY_BUTTON_TITLE_MAX}`}
                  FormHelperTextProps={{ sx: { textAlign: "right", mr: 0 } }}
                />
                <Tooltip
                  title={
                    linkSwitchDisabled
                      ? `Já existe um botão com link neste passo (limite do WhatsApp: ${WHATSAPP_MAX_LINK_BUTTONS}).`
                      : ""
                  }
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={isLink}
                          disabled={linkSwitchDisabled}
                          onChange={(e) => toggleBotaoLink(i, e.target.checked)}
                        />
                      }
                      label="Link"
                      sx={{ mr: 0, whiteSpace: "nowrap" }}
                    />
                  </span>
                </Tooltip>
                <IconButton size="small" onClick={() => removeBotao(i)} color="error" aria-label="Remover botão">
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
              {isLink ? (
                <TextField
                  size="small"
                  fullWidth
                  value={b.url || ""}
                  onChange={(e) => changeBotaoUrl(i, e.target.value)}
                  placeholder="https://exemplo.com"
                  variant="outlined"
                  type="url"
                  InputProps={{
                    endAdornment: String(b.url || "").trim() ? (
                      <InputAdornment position="end">
                        <Tooltip title="Regras e possibilidades para links no WhatsApp">
                          <IconButton
                            size="small"
                            onClick={() => setLinkHelpOpen(true)}
                            aria-label="Instruções sobre botões com link"
                            edge="end"
                          >
                            <InfoOutlined fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              ) : null}
            </Box>
            );
          })}
          <Button
            variant="outlined"
            size="medium"
            startIcon={<Add />}
            onClick={addBotao}
            sx={{ alignSelf: "flex-start", mt: 0.5, textTransform: "none" }}
          >
            Adicionar botão
          </Button>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
          Ações
        </Typography>
        <ChatbotStepActionEditor
          acoes={form.acoes}
          onAcoesChange={(next) =>
            setForm((f) => ({
              ...f,
              acoes: typeof next === "function" ? next(f.acoes || []) : next,
            }))
          }
          actions={acoesEnriched}
          isLoading={isLoadingAcoes}
          onNavigateToSlot={onNavigateToSlot}
          slots={allSlots}
        />

        {/* Seções avançadas */}
        <Divider sx={{ my: 3 }} />
        <Button
          size="small"
          variant="text"
          onClick={() => setShowAdvanced((v) => !v)}
          endIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
          sx={{ alignSelf: "flex-start", textTransform: "none", mb: 1 }}
        >
          {showAdvanced ? "Ocultar opções avançadas" : "Mostrar opções avançadas"}
        </Button>
        <Collapse in={showAdvanced} unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
              Arquivo de resposta (opcional)
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                disabled={isUploadingFile}
                sx={{ textTransform: "none" }}
              >
                {isUploadingFile ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                {isUploadingFile ? "Enviando..." : "Selecionar arquivo"}
                <input type="file" hidden onChange={handleSelectFile} />
              </Button>
              {form.arquivo && (
                <Tooltip title={form.arquivo}>
                  <Chip
                    label="Arquivo associado"
                    color="primary"
                    variant="outlined"
                    onDelete={() => setForm((f) => ({ ...f, arquivo: "" }))}
                  />
                </Tooltip>
              )}
            </Stack>

            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
              Condições
            </Typography>
            <Box sx={{ mb: 3 }}>
              <ConditionsEditor
                key={`cond-${slot?.id ?? "new"}`}
                form={form}
                setForm={setForm}
                isFlowStart={isFlowStart}
              />
            </Box>

            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
              Horários por dia da semana
            </Typography>
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formHasHorarioRestriction(form)}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setForm((f) => {
                        if (!on) return { ...f, ...clearAllHorarioFields() };
                        if (formHasHorarioRestriction(f)) return f;
                        return {
                          ...f,
                          horario_seg_ini: "09:00",
                          horario_seg_fim: "18:00",
                        };
                      });
                    }}
                  />
                }
                label="Restringir este passo por horário"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mb: 1, display: "block" }}>
                Desligado = válido o dia todo. Ligado = escolha os dias e o intervalo em cada um.
              </Typography>
              {formHasHorarioRestriction(form) ? (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {CHATBOT_HORARIO_DAYS.map((row) => {
                    const active = dayHorarioActive(form, row.keyIni, row.keyFim);
                    return (
                      <Box
                        key={row.keyIni}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          py: 0.25,
                        }}
                      >
                        <FormControlLabel
                          sx={{ minWidth: 148, mr: 0.5 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={active}
                            onChange={(e) => {
                              const on = e.target.checked;
                              setForm((f) =>
                                on
                                  ? {
                                      ...f,
                                      [row.keyIni]: "09:00",
                                      [row.keyFim]: "18:00",
                                    }
                                  : { ...f, [row.keyIni]: "", [row.keyFim]: "" }
                              );
                            }}
                            />
                          }
                          label={<Typography variant="body2">{row.label}</Typography>}
                        />
                        {active ? (
                          <>
                            <TextField
                              type="time"
                              size="small"
                              label="Início"
                              value={form[row.keyIni] || ""}
                              onChange={(e) =>
                                setForm((f) => ({ ...f, [row.keyIni]: e.target.value || "" }))
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 130 }}
                            />
                            <TextField
                              type="time"
                              size="small"
                              label="Fim"
                              value={form[row.keyFim] || ""}
                              onChange={(e) =>
                                setForm((f) => ({ ...f, [row.keyFim]: e.target.value || "" }))
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 130 }}
                            />
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Sem restrição neste dia
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              ) : null}
            </Box>

            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
              Comportamento no fluxo
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1.45 }}>
              {isFlowStart
                ? "Obrigatório impede pular este passo por intenção na primeira mensagem. Ignorar global faz valer só os subpassos diretos enquanto a conversa estiver aqui."
                : "Slots globais são acionados em qualquer nível da conversa. Use \"Ignorar global\" em um passo quando quiser que, nesse ponto, apenas os subpassos diretos sejam considerados."}
            </Typography>
            {form.global ? (
              <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="chatbot-evento-tipo-label">Acionamento</InputLabel>
                  <Select
                    labelId="chatbot-evento-tipo-label"
                    label="Acionamento"
                    value={form.evento?.tipo || EVENTO_IDENTIFICADOR}
                    onChange={(e) => {
                      const tipo = e.target.value;
                      setForm((f) => ({
                        ...f,
                        evento:
                          tipo === EVENTO_INATIVIDADE
                            ? {
                                tipo: EVENTO_INATIVIDADE,
                                minutos_inatividade: f.evento?.minutos_inatividade || 3,
                              }
                            : { tipo: EVENTO_IDENTIFICADOR },
                      }));
                    }}
                  >
                    <MenuItem value={EVENTO_IDENTIFICADOR}>Identificador (resposta do cliente)</MenuItem>
                    <MenuItem value={EVENTO_INATIVIDADE}>Inatividade</MenuItem>
                  </Select>
                </FormControl>
                {form.evento?.tipo === EVENTO_INATIVIDADE ? (
                  <TextField
                    type="number"
                    label="Tempo sem resposta (minutos)"
                    size="small"
                    fullWidth
                    inputProps={{ min: 1, step: 1 }}
                    value={form.evento?.minutos_inatividade ?? 3}
                    onChange={(e) => {
                      const min = Math.max(1, parseInt(e.target.value, 10) || 1);
                      setForm((f) => ({
                        ...f,
                        evento: { ...f.evento, tipo: EVENTO_INATIVIDADE, minutos_inatividade: min },
                      }));
                    }}
                    helperText="Executa após esse tempo aguardando resposta. Não muda o passo atual, salvo se houver ação Ir para."
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block">
                    O passo é acionado quando o cliente envia texto que corresponde ao identificador.
                  </Typography>
                )}
              </Box>
            ) : null}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              {!isFlowStart ? (
              <Tooltip
                title="Este passo poderá ser acionado em qualquer nível do fluxo (o usuário pode digitar o identificador mesmo estando em outro passo)."
                placement="top"
              >
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.global}
                        onChange={(e) => {
                          setTouched((t) => ({ ...t, global: true }));
                          setForm((f) => ({ ...f, global: e.target.checked }));
                        }}
                      />
                    }
                    label="Global"
                  />
                  <InfoOutlined sx={{ fontSize: 16, color: "action.active" }} />
                </Box>
              </Tooltip>
              ) : null}
              <Tooltip
                title="Quando a conversa estiver neste passo, os slots marcados como Global não serão considerados; apenas os subpassos diretos deste nó."
                placement="top"
              >
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.ignorarglobal}
                        onChange={(e) => {
                          setTouched((t) => ({ ...t, ignorarglobal: true }));
                          setForm((f) => ({ ...f, ignorarglobal: e.target.checked }));
                        }}
                      />
                    }
                    label="Ignorar global"
                  />
                  <InfoOutlined sx={{ fontSize: 16, color: "action.active" }} />
                </Box>
              </Tooltip>
              <Tooltip
                title="Enquanto o cliente não escolher uma das opções (subpassos) deste nó, a conversa não avança. Respostas fora das opções usam o passo Exceção do nível (ou o global)."
                placement="top"
              >
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.obrigatorio}
                        onChange={(e) => {
                          setForm((f) => ({ ...f, obrigatorio: e.target.checked }));
                        }}
                      />
                    }
                    label="Obrigatório"
                  />
                  <InfoOutlined sx={{ fontSize: 16, color: "action.active" }} />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Rodapé fixo com ações principais */}
      <Box
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <CircularProgress size={22} /> : "Salvar"}
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Fechar
          </Button>
          {canDelete && (
            <Button variant="outlined" color="error" onClick={handleDelete} disabled={isDeleting} sx={{ ml: { xs: 0, sm: "auto" } }}>
              {isDeleting ? (
                <CircularProgress size={20} />
              ) : isLocalExcecao ? (
                t("chatbotConfigPage.deleteLocalExceptionButton", { defaultValue: "Excluir exceção" })
              ) : (
                t("chatbotConfigPage.deleteSlotButton", { defaultValue: "Excluir" })
              )}
            </Button>
          )}
        </Stack>
      </Box>
      <ChatbotButtonLinkHelpDialog open={linkHelpOpen} onClose={() => setLinkHelpOpen(false)} />
      {/* Confirmação de exclusão */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !isDeleting && setConfirmDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {isLocalExcecao
            ? t("chatbotConfigPage.confirmDeleteLocalExceptionTitle", {
                defaultValue: "Excluir exceção deste nível?",
              })
            : t("chatbotConfigPage.confirmDeleteSlotTitle", { defaultValue: "Excluir passo do ChatBot?" })}
        </DialogTitle>
        <DialogContent dividers>
          {isLocalExcecao ? (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                {t("chatbotConfigPage.confirmDeleteLocalExceptionBody", {
                  defaultValue:
                    "Entradas inválidas neste nível passarão a usar a exceção global do fluxo (sob Início), quando configurada.",
                })}
              </Typography>
              {!globalExceptionReady && (
                <Alert severity="warning">
                  {t("chatbotConfigPage.confirmDeleteLocalExceptionNoGlobal", {
                    defaultValue:
                      "A exceção global ainda não está configurada. Após excluir, entradas inválidas neste nível podem ficar sem resposta de fallback.",
                  })}
                </Alert>
              )}
            </Stack>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Este passo faz parte do fluxo automatizado do seu ChatBot. Ao prosseguir com a exclusão,{" "}
                <strong>todos os subpassos ligados a ele também serão removidos</strong>, incluindo suas mensagens,
                botões, condições e configurações avançadas.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Se você não tiver certeza, cancele agora e revise o fluxo antes de tomar essa decisão. Caso confirme,
                a exclusão não poderá ser desfeita diretamente pela interface.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (!slot || !canDelete) return;
              onDelete(slot.id);
              setConfirmDeleteOpen(false);
            }}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isLocalExcecao
              ? t("chatbotConfigPage.confirmDeleteLocalExceptionButton", {
                  defaultValue: "Excluir exceção",
                })
              : t("chatbotConfigPage.confirmDeleteSlotButton", {
                  defaultValue: "Excluir passo e subpassos",
                })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (presentation === "embedded") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          bgcolor: "background.paper",
          color: "text.primary",
        }}
      >
        {panelBody}
      </Box>
    );
  }

  const drawerZIndex = presentation === "overlay" ? 1400 : 1250;
  const backdropColor = presentation === "overlay" ? "rgba(0, 0, 0, 0.12)" : "rgba(0, 0, 0, 0.65)";

  return (
    <Drawer
      anchor="right"
      open={!!slot}
      onClose={onClose}
      ModalProps={{
        sx: { zIndex: drawerZIndex },
        slotProps: {
          backdrop: { sx: { backgroundColor: backdropColor } },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480 },
          maxWidth: "100vw",
          height: "100vh",
          maxHeight: "100vh",
          boxShadow: (t) => t.shadows[8],
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          color: "text.primary",
        },
      }}
    >
      {panelBody}
    </Drawer>
  );
}
